import {
  createCustomField,
  getCustomFields,
  updateCustomField,
} from '@/api/customFields';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { CustomFieldsAPIResponse } from '@/types';
import { handleAPIError } from '@/utils/errorHandler';
import { useAuth } from '@/providers/AuthProvider';

interface CustomFieldProps {
  entity?: string;
  projectType?: string;
  options?: Partial<UseQueryOptions<CustomFieldsAPIResponse, Error>>;
}

export const useCustomFields = ({ entity }: CustomFieldProps) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['customFields', orgId],
    queryFn: getCustomFields,
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    customFields: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};
export const useCreateCustomField = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  // Adds a custom field
  const mutation = useMutation({
    mutationFn: createCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customFields', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['projectTypes', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    createCustomField: mutation.mutate,
    createCustomFieldAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useUpdateCustomField = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: updateCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projectTypes', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['projects', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['customFields', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    updateCustomField: mutation.mutate,
    updateCustomFieldAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
